import { motion } from "framer-motion";
import { Check, ArrowRight, PenLine, Sparkles, SpellCheck2, Lightbulb, Network } from "lucide-react";
import { INTRO_KEY } from "@/components/OrientationLayer";

// Persistent workflow orientation for the learner: WHERE they are, WHAT comes next, HOW much remains.

const STAGES = [
  { key: "composition", label: "Composition" },
  { key: "sentence_craft", label: "Sentence Craft" },
  { key: "proofreading", label: "Proofreading" },
  { key: "final", label: "Final Review" },
];

// YOUR PROGRESS — real navigation. Any stage the learner has already REACHED is clickable and
// returns them to that stage's shared writing (a VIEW/EDIT surface — it never restarts coaching on
// its own). ✓ completed · ● current · ○ remaining. `stale` marks a completed stage whose text has
// been edited since (quiet orientation, never a warning). `viewing` is the stage being looked back at.
export const StageStepper = ({ current, reached = [], stale = [], viewing = null, onNavigate }) => {
  const idx = STAGES.findIndex((s) => s.key === current);
  const reachedIdx = STAGES.reduce((m, s, i) => (reached.includes(s.key) || i <= idx ? Math.max(m, i) : m), idx);
  return (
    <nav
      data-testid="stage-stepper"
      aria-label="Your progress"
      className="w-full flex flex-col items-center gap-1 py-2.5 mb-4 border-b border-stone-200"
    >
      <span className="font-mono-panel text-[9.5px] uppercase tracking-[0.22em] text-stone-400 mb-0.5">Your progress</span>
      <div className="flex items-center justify-center gap-1 sm:gap-1.5 flex-wrap">
        {STAGES.map((s, i) => {
          const done = i < idx;
          const active = i === idx;
          const reachable = i <= reachedIdx;
          const isViewing = viewing === s.key;
          const isStale = stale.includes(s.key);
          const mark = done ? "✓" : active ? "●" : "○";
          const Tag = reachable && onNavigate ? "button" : "span";
          return (
            <div key={s.key} className="flex items-center gap-1 sm:gap-1.5">
              <Tag
                {...(reachable && onNavigate ? { onClick: () => onNavigate(s.key), type: "button" } : {})}
                data-testid={`stage-step-${s.key}`}
                data-state={active ? "active" : done ? "done" : "upcoming"}
                aria-current={active ? "step" : undefined}
                title={reachable && onNavigate ? `Go to ${s.label}` : undefined}
                className={
                  "relative inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10.5px] sm:text-[11.5px] font-mono-panel uppercase tracking-[0.12em] transition-colors " +
                  (reachable && onNavigate ? "cursor-pointer hover:ring-1 hover:ring-[#8C3A2A]/40 " : "") +
                  (isViewing
                    ? "bg-[#8C3A2A]/15 text-[#8C3A2A] font-semibold ring-1 ring-[#8C3A2A]/50"
                    : active
                    ? "bg-[#8C3A2A]/10 text-[#8C3A2A] font-semibold ring-1 ring-[#8C3A2A]/30"
                    : done
                    ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100"
                    : "bg-stone-100 text-stone-400")
                }
              >
                <span aria-hidden className="text-[11px] leading-none">{mark}</span>
                {s.label}
                {isStale && (
                  <span
                    data-testid={`stage-stale-${s.key}`}
                    title="Edited since — resume this stage to re-check"
                    className="ml-1 inline-flex items-center gap-0.5 text-[8.5px] normal-case tracking-normal text-amber-600"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> edited since
                  </span>
                )}
              </Tag>
              {i < STAGES.length - 1 && (
                <span aria-hidden className={"text-[12px] " + (i < idx ? "text-emerald-400" : "text-stone-300")}>
                  &rarr;
                </span>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
};

// FIRST-ENCOUNTER ORIENTATION (Beta 1) — the five-part student-facing journey (see lib/orientation.js).
const ROADMAP_STAGES = [
  { icon: Lightbulb, title: "Develop", body: "Work out what you want to say." },
  { icon: Network, title: "Organize", body: "Figure out how your ideas fit together." },
  { icon: PenLine, title: "Write for a reader", body: "Turn your thinking into writing someone else can follow." },
  { icon: Sparkles, title: "Refine", body: "Make your sentences clearer." },
  { icon: SpellCheck2, title: "Finish", body: "Give your writing a final check." },
];

export const SessionRoadmap = ({ onStart }) => (
  <div
    data-testid="session-roadmap"
    className="fixed inset-0 z-50 flex items-center justify-center bg-[#faf9f6]/95 backdrop-blur-sm px-6 py-10 overflow-y-auto"
  >
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="w-full max-w-lg bg-white border border-stone-200 rounded-md shadow-xl p-8"
    >
      <p className="font-mono-panel text-[11px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-2">How The Writing Studio works</p>
      <h2 className="font-serif-display text-[1.7rem] leading-tight text-stone-900 mb-2">
        You'll develop your ideas, organize them, write them for a reader, refine the writing, and finish.
      </h2>
      <p className="text-[13.5px] text-stone-600 leading-relaxed mb-1" data-testid="roadmap-nonlinear-note">Writing isn't perfectly linear. You may move back and forth as your ideas develop.</p>
      <p className="text-[13.5px] text-stone-600 leading-relaxed mb-5">You don't need to manage the whole process. TWS will help you focus on what matters now.</p>
      <ol className="space-y-4 mb-7">
        {ROADMAP_STAGES.map((s, i) => {
          const Icon = s.icon;
          return (
            <li key={s.title} className="flex items-start gap-3.5" data-testid={`roadmap-stage-${i + 1}`}>
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#8C3A2A]/[0.08] text-[#8C3A2A]">
                <Icon className="h-4 w-4" />
              </span>
              <div>
                <p className="font-serif-display text-[16px] font-semibold text-stone-900 leading-snug">{s.title}</p>
                <p className="text-[13.5px] text-stone-600 leading-snug">{s.body}</p>
              </div>
            </li>
          );
        })}
      </ol>
      <div className="mb-5 rounded-md border border-stone-200 bg-stone-50/70 p-4" data-testid="roadmap-authorship-note">
        <p className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-stone-500 mb-1.5">About your writing</p>
        <p className="text-[13px] text-stone-700 leading-relaxed mb-2" data-testid="roadmap-scope-note">
          <span className="font-semibold text-stone-900">The Writing Studio works with one paragraph at a time.</span> You may be writing a single-paragraph assignment or working on one paragraph from a longer paper &mdash; either way, it focuses on the paragraph you&rsquo;re working on now, helping you develop its ideas, organization, support, sentences, and proofreading. It doesn&rsquo;t yet organize a full essay or other multi-paragraph paper.
        </p>
        <p className="text-[13px] text-stone-600 leading-relaxed">
          The Writing Studio helps you develop <span className="font-medium text-stone-800">your own</span> writing &mdash; it doesn&rsquo;t write for you. You&rsquo;ll type your writing in directly (rather than pasting it in), and The Writing Studio follows how your ideas and writing develop as you work. That&rsquo;s how it establishes that the writing reflects your own work, which also helps your teacher see your progress. If The Writing Studio can&rsquo;t connect part of your writing to the work you&rsquo;ve shown, it may ask you to develop that part further. No single signal &mdash; including AI detectors, which often get it wrong &mdash; decides this.
        </p>
      </div>
      <p className="text-[13px] text-stone-500 italic mb-6">You decide how far to go at each stage.</p>
      <button
        onClick={() => { try { localStorage.setItem(INTRO_KEY, "1"); } catch { /* ignore */ } onStart(); }}
        data-testid="session-roadmap-start"
        className="group inline-flex items-center gap-2.5 bg-[#8C3A2A] text-white px-7 py-3.5 rounded-sm font-medium tracking-wide uppercase text-[13px] hover:bg-[#6B2C20] hover:-translate-y-px transition-[background-color,transform]"
      >
        Start Writing
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </button>
    </motion.div>
  </div>
);

export const ModeBanner = ({ mode }) => {
  const cfg = {
    compose: {
      label: "Composition & Revision",
      hint: "Edit your paragraph above to develop your ideas, then press \u201cSend revision.\u201d",
    },
    revise: {
      label: "Sentence Craft",
      hint: "Revise the highlighted sentence above to express it more clearly, then press \u201cSend revision.\u201d",
    },
    proofread: {
      label: "Proofreading",
      hint: "Fix the highlighted issue in your paragraph above, then press \u201cSend corrected paragraph.\u201d",
    },
    discuss: {
      label: "Discussion",
      hint: "Type your answer in the conversation box below and send it.",
    },
  }[mode];
  if (!cfg) return null;
  return (
    <div
      data-testid="mode-banner"
      data-mode={mode}
      className="mb-3 flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-sm border border-stone-200 bg-white px-4 py-2.5"
    >
      <span className="inline-flex items-center rounded-full bg-[#8C3A2A]/[0.08] px-2.5 py-0.5 font-mono-panel text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8C3A2A]">
        {cfg.label}
      </span>
      <span className="text-[13px] text-stone-600">{cfg.hint}</span>
    </div>
  );
};

export const NextAction = ({ text }) => (
  <div
    data-testid="next-action"
    className="mt-4 rounded-md border-l-2 border-[#8C3A2A] bg-[#8C3A2A]/[0.05] px-4 py-3"
  >
    <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-[#8C3A2A] font-semibold mb-1">
      What to do now
    </p>
    <p className="text-[14px] text-stone-800 leading-snug">{text}</p>
  </div>
);
